import React, { memo } from "react";
import { View } from "react-native";
import { AppText } from "@/components/ui/AppText";
import { Button } from "@/components/ui/Button";
import { useI18n } from "@/context/I18nContext";
import type { TranslationKeys } from "@chinese-vendor/i18n";
import { cn } from "@/lib/utils";

export interface EmptyStateAction {
  /** Button label (translation key). */
  label: TranslationKeys;
  onPress: () => void;
}

export interface EmptyStateProps {
  /**
   * Bold heading (translation key).
   * Defaults to `emptyState.title` — "Nothing here yet".
   */
  title?: TranslationKeys;
  /**
   * Supporting description below the heading (translation key).
   * Defaults to `emptyState.description`.
   */
  description?: TranslationKeys;
  /**
   * Optional primary action button rendered below the description.
   * Pass `undefined` to suppress the button.
   */
  action?: EmptyStateAction;
  /**
   * Custom icon node rendered above the heading.
   * When omitted a default illustration symbol is shown.
   */
  icon?: React.ReactNode;
  /** Forwarded to the root View for testing. */
  testID?: string;
  className?: string;
}

/**
 * Full-width empty state — shown when a list or section has no content.
 *
 * All text props are optional and fall back to the `emptyState.*` i18n keys so
 * the component is usable with zero props:
 *
 * @example
 * // Zero-prop defaults
 * <EmptyState />
 *
 * @example
 * // With custom copy and an action
 * <EmptyState
 *   title="review.emptyDashboard.title"
 *   description="review.emptyDashboard.description"
 *   action={{ label: "common.refresh", onPress: refetch }}
 * />
 */
export const EmptyState = memo(function EmptyState({
  title = "emptyState.title",
  description = "emptyState.description",
  action,
  icon,
  testID,
  className,
}: EmptyStateProps) {
  const { t } = useI18n();

  return (
    <View
      testID={testID}
      className={cn("flex-1 items-center justify-center px-8 py-12 gap-4", className)}
      accessibilityRole="none"
    >
      {/* ── Icon ─────────────────────────────────────────────────────────── */}
      {icon != null ? (
        icon
      ) : (
        <View className="size-16 items-center justify-center" accessible={false}>
          <AppText weight="regular" className="text-5xl" accessible={false}>
            📭
          </AppText>
        </View>
      )}

      {/* ── Text ─────────────────────────────────────────────────────────── */}
      <View className="items-center gap-2">
        <AppText weight="bold" className="text-lg text-dark-text-50 text-center">
          {t(title)}
        </AppText>
        <AppText weight="regular" className="text-sm text-supporting-text text-center leading-5">
          {t(description)}
        </AppText>
      </View>

      {/* ── Action button ────────────────────────────────────────────────── */}
      {action != null && (
        <Button
          label={action.label}
          variant="primary"
          onPress={action.onPress}
          testID={testID != null ? `${testID}-action` : undefined}
        />
      )}
    </View>
  );
});
